/*
 * Copyright (c) 2022. Revo Digital
 * ---
 * Author: gabriele
 * File: LinkDynamicPanel.tsx
 * Project: mle-client-n
 * Committed last: 2022/5/4 @ 1724
 * ---
 * Description:
 */

import {observer} from 'mobx-react';
import {Editor} from '../../../../store/observables/Editor';
import {ItemRenderer, Select} from '@blueprintjs/select';
import {Button, MenuItem, Spinner} from '@blueprintjs/core';
import {useCallback, useEffect, useState} from 'react';
import {DataOrigin} from '@revodigital/mle-dataresolver/lib/entities';
import {mleDomainGqlClient} from '../../../../api/domain';
import {GET_AVAILABLE_DY_GROUPS, GET_DATAORIGINS_BY_COMPANY_ID} from '../../../../api/domain/queries.gql';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../store';
import {DynamicDataRef, IDataRef} from '@revodigital/mpes';
import {DataRefGroup} from '../../../../entities';
import {Tooltip2} from '@blueprintjs/popover2';
import {MyLabelNotConnected} from '../../../common/callout/MyLabelNotConnected';
import {LinkDataOriginDialog} from '../../dialogs/data-origin/LinkDataOriginDialog';
import {mlAuthSecured} from '../../../../store/features/mylabel/auth/MyLabelAuthFeature';

interface Props {
  editor: Editor;
  onDataOriginChange: (dataRef: IDataRef | undefined) => void;
}

const FieldSelect = Select.ofType<DataRefGroup>();

export const LinkDynamicPanel = observer((props: Props) => {
  const authData = useSelector((state: RootState) => state.authState);
  const [selectedField, setSelectedField] = useState('');
  const [selectedOrigin, setSelectedOrigin] = useState<DataOrigin | undefined>();
  const [fieldsList, setFieldsList] = useState<DataRefGroup[]>([]);
  const [isLoadingFields, setIsLoadingFields] = useState(false);
  const [connectMlVisible, setConnectMlVisible] = useState(false);

  const fieldGroupRenderer: ItemRenderer<DataRefGroup> = (rootItem, {
    handleClick,
    index
  }) => {
    if (Array.isArray(rootItem.keys) && !!rootItem.keys)
      return (
          <MenuItem text={rootItem.groupName}>
            {
              rootItem.keys?.map(child => <Tooltip2
                  content={child.value as string}>
                <MenuItem
                    text={child.key}
                    onClick={() => {
                      handleSelectField(
                          child.key);
                      handleClick(new MouseEvent(
                          child.key) as any);
                    }}/>
              </Tooltip2>)
            }
          </MenuItem>
      );
    return null;
  };

  const handleSelectField = useCallback((key: string) => {
    setSelectedField(key);

    // Create dataRef
    if (props.editor.recipeSwitcher.hasActiveRevision) {
      const dataRef = new DynamicDataRef(selectedOrigin?.id.toString() as string,
          key);

      props.onDataOriginChange(dataRef);
    }

  }, [selectedField, setSelectedField, selectedOrigin]);

  useEffect(() => {
        if (props.editor.mylabelAuthFeature.isConnected && props.editor.recipeSwitcher.hasActiveRevision) {
          (async () => {
            setIsLoadingFields(true);
            const origins = (await mleDomainGqlClient.query({
              query: GET_DATAORIGINS_BY_COMPANY_ID,
              variables: {
                id: authData.user.company?.id
              },
              fetchPolicy: 'no-cache'
            })).data.company.dataOrigins;

            if (origins.length > 0)
              setSelectedOrigin(origins[0]);

            const groups = (await mlAuthSecured<DataRefGroup[]>(props.editor, async () => {
              return (await mleDomainGqlClient.query({
                query: GET_AVAILABLE_DY_GROUPS,
                variables: {
                  revisionId: props.editor.recipeSwitcher.activeRevision,
                  companyId: props.editor.recipeSwitcher.activeCompany,
                },
              })).data.getAvailableDynamicGroups;
            }));

            if (groups)
              setFieldsList(groups);

            setIsLoadingFields(false);
          })();
      }
    },
    [setFieldsList, setSelectedOrigin, props.editor.mylabelAuthFeature.isConnected]);

  const handleConnectMyLabel = useCallback(() => {
    setConnectMlVisible(true);
  }, []);

  return (
    <div>
      {
        !props.editor.mylabelAuthFeature.isConnected &&
        <MyLabelNotConnected onConnect={ () => setConnectMlVisible(true) }/>
      }
      <div className="text-bpgray-200">Un'origine dati derivata permette di
        inserire nell'etichetta dei valori dinamici
        collegati a MyLabel
      </div>
      <div>
        <div className="text-xl my-2">Campo dati</div>
        <div className="text-bpgray-200 mb-2">Seleziona un campo dell'origine
        </div>
        <FieldSelect
          disabled={ isLoadingFields }
          filterable={ false }
          itemRenderer={ fieldGroupRenderer }
          items={ fieldsList }
          onItemSelect={ (item) => {} }>
          <Button
            disabled={ isLoadingFields || !props.editor.mylabelAuthFeature.isConnected }
            rightIcon="caret-down"> { isLoadingFields ? <Spinner
            size={ 20 }/> : <>{ selectedField ? selectedField : 'Seleziona un campo' }</> }
          </Button>
        </FieldSelect>
      </div>
      {
        connectMlVisible && <LinkDataOriginDialog editor={ props.editor }
                                                  isOpen={ connectMlVisible }
                                                  onClose={ () => setConnectMlVisible(
                                                    false) }/>
      }
    </div>
  );
});
